import { DecimalValue } from '../types/restaurant/RestaurantProductionInventoryTypes';

const POWERS_OF_TEN: bigint[] = [1n];

const powerOfTen = (scale: number) => {
  while (POWERS_OF_TEN.length <= scale) {
    POWERS_OF_TEN.push(POWERS_OF_TEN[POWERS_OF_TEN.length - 1] * 10n);
  }
  return POWERS_OF_TEN[scale];
};

const expandExponential = (value: string) => {
  if (!/[eE]/.test(value)) return value;
  const [coefficient, exponentText] = value.toLowerCase().split('e');
  const exponent = Number.parseInt(exponentText, 10);
  if (!Number.isFinite(exponent)) return value;
  const negative = coefficient.startsWith('-');
  const digits = coefficient.replace('-', '').replace('.', '');
  const decimalIndex = coefficient.replace('-', '').indexOf('.');
  const initialScale =
    decimalIndex < 0
      ? 0
      : coefficient.replace('-', '').length - decimalIndex - 1;
  const resultingScale = initialScale - exponent;
  const sign = negative ? '-' : '';
  if (resultingScale <= 0)
    return `${sign}${digits}${'0'.repeat(-resultingScale)}`;
  if (resultingScale >= digits.length)
    return `${sign}0.${'0'.repeat(resultingScale - digits.length)}${digits}`;
  return `${sign}${digits.slice(
    0,
    digits.length - resultingScale
  )}.${digits.slice(digits.length - resultingScale)}`;
};

const parseDecimal = (value: DecimalValue) => {
  const normalized = expandExponential(String(value).trim() || '0');
  const negative = normalized.startsWith('-');
  const unsigned = normalized.replace(/^[+-]/, '');
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const digits = `${whole || '0'}${fraction}`.replace(/^0+(?=\d)/, '');
  return {
    integer: BigInt(`${negative ? '-' : ''}${digits || '0'}`),
    scale: fraction.length,
  };
};

const serializeDecimal = (integer: bigint, scale: number) => {
  const negative = integer < 0n;
  const digits = (negative ? -integer : integer)
    .toString()
    .padStart(scale + 1, '0');
  const value = scale
    ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}`.replace(/\.?0+$/, '')
    : digits;
  return `${negative ? '-' : ''}${value || '0'}`;
};

export const multiplyDecimal = (left: DecimalValue, right: DecimalValue) => {
  const a = parseDecimal(left);
  const b = parseDecimal(right);
  return serializeDecimal(a.integer * b.integer, a.scale + b.scale);
};

export const addDecimal = (left: DecimalValue, right: DecimalValue) => {
  const a = parseDecimal(left);
  const b = parseDecimal(right);
  const scale = Math.max(a.scale, b.scale);
  return serializeDecimal(
    a.integer * powerOfTen(scale - a.scale) +
      b.integer * powerOfTen(scale - b.scale),
    scale
  );
};

export const compareDecimal = (left: DecimalValue, right: DecimalValue) => {
  const a = parseDecimal(left);
  const b = parseDecimal(right);
  const scale = Math.max(a.scale, b.scale);
  const normalizedA = a.integer * powerOfTen(scale - a.scale);
  const normalizedB = b.integer * powerOfTen(scale - b.scale);
  return normalizedA === normalizedB ? 0 : normalizedA > normalizedB ? 1 : -1;
};

export const sumDecimalProducts = (
  values: Array<{ quantity: DecimalValue; unitCost: DecimalValue }>
) =>
  values.reduce(
    (total, item) =>
      addDecimal(total, multiplyDecimal(item.quantity, item.unitCost)),
    '0'
  );

export const formatDecimal = (
  value: DecimalValue,
  maximumFractionDigits = 6
) => {
  const text = String(value ?? 0);
  const numeric = Number(text);
  return Number.isFinite(numeric)
    ? numeric.toLocaleString('es-SV', { maximumFractionDigits })
    : text;
};

export const formatMoney = (value: DecimalValue) => {
  const numeric = Number(String(value ?? 0));
  return Number.isFinite(numeric)
    ? new Intl.NumberFormat('es-SV', {
        style: 'currency',
        currency: 'USD',
      }).format(numeric)
    : `$${String(value)}`;
};
